import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Filter,
  Grid3X3,
  List,
  Star,
  Users,
  MapPin,
  DollarSign,
  Clock,
  Tag,
  TrendingUp,
  Award,
  Heart,
  Share2,
  ExternalLink,
  Building2,
  Calendar,
  Package,
  ChevronRight,
  Eye,
  BookOpen,
  Zap,
  ShoppingCart,
  ThumbsUp,
  MessageSquare
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useServiceCategories } from "@/hooks/useServiceCategories";
import type { ServiceCategory } from "@/types/serviceCategories";
import { CardLoading, EmptyState } from "@/components/ui/LoadingStates";

interface Service {
  id: string;
  name: string;
  description: string;
  categoryId: string;
  categoryName: string;
  vendorId: string;
  vendorName: string;
  vendorLogo?: string;
  vendorRating: number;
  basePrice: number;
  currency: string;
  duration: string;
  capacity: {
    min: number;
    max: number;
  };
  location: string;
  availability: 'high' | 'medium' | 'low';
  featured: boolean;
  tags: string[];
  images: string[];
  reviewCount: number;
  averageRating: number;
  lastBookedAt?: string;
}

interface ServiceCategoryBrowserProps {
  onServiceSelect?: (service: Service) => void;
  onCategorySelect?: (category: ServiceCategory) => void;
  allowMultiSelect?: boolean;
  showPricing?: boolean;
  maxSelection?: number;
  filterByLocation?: string;
  filterByBudget?: { min: number; max: number };
  className?: string;
}

const mockServices: Service[] = [
  {
    id: "escape-room-dubai",
    name: "Corporate Escape Room Challenge",
    description: "Interactive team-building escape room experience designed for corporate groups",
    categoryId: "team-building",
    categoryName: "Team Building",
    vendorId: "vendor-1",
    vendorName: "Quest Adventures Dubai",
    vendorLogo: "/api/placeholder/40/40",
    vendorRating: 4.8,
    basePrice: 120,
    currency: "AED",
    duration: "2 hours",
    capacity: { min: 6, max: 20 },
    location: "Dubai Marina",
    availability: "high",
    featured: true,
    tags: ["problem-solving", "communication", "leadership"],
    images: ["/api/placeholder/300/200"],
    reviewCount: 156,
    averageRating: 4.7,
    lastBookedAt: "2024-01-10T14:30:00Z"
  },
  {
    id: "leadership-workshop",
    name: "Executive Leadership Workshop",
    description: "Comprehensive leadership development program for senior management",
    categoryId: "corporate-training",
    categoryName: "Corporate Training",
    vendorId: "vendor-2",
    vendorName: "Leadership Excellence Institute",
    vendorRating: 4.9,
    basePrice: 450,
    currency: "AED",
    duration: "Full day",
    capacity: { min: 8, max: 25 },
    location: "Dubai Business Bay",
    availability: "medium",
    featured: false,
    tags: ["leadership", "management", "strategy"],
    images: ["/api/placeholder/300/200"],
    reviewCount: 89,
    averageRating: 4.8
  },
  {
    id: "yoga-session",
    name: "Corporate Wellness Yoga",
    description: "Relaxing yoga sessions designed to reduce workplace stress",
    categoryId: "wellness",
    categoryName: "Wellness & Health",
    vendorId: "vendor-3",
    vendorName: "Zen Wellness Studio",
    vendorRating: 4.6,
    basePrice: 75,
    currency: "AED",
    duration: "1 hour",
    capacity: { min: 5, max: 30 },
    location: "Multiple locations",
    availability: "high",
    featured: true,
    tags: ["wellness", "stress-relief", "mindfulness"],
    images: ["/api/placeholder/300/200"],
    reviewCount: 203,
    averageRating: 4.5
  }
];

export default function ServiceCategoryBrowser({
  onServiceSelect,
  onCategorySelect,
  allowMultiSelect = false,
  showPricing = true,
  maxSelection = 5,
  filterByLocation,
  filterByBudget,
  className
}: ServiceCategoryBrowserProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState("popularity");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showServiceDetail, setShowServiceDetail] = useState<Service | null>(null);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });

  const { toast } = useToast();

  // Real service categories (GET /marketplace/service-categories). Match on id, never
  // on name — category names are localized, so a name-match would break under translation.
  const { data: categories = [], isLoading: categoriesLoading } = useServiceCategories();

  const filteredCategories = useMemo(() => {
    return categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description ?? "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  const filteredServices = useMemo(() => {
    let services = mockServices;

    if (selectedCategory) {
      services = services.filter(service => service.categoryId === selectedCategory);
    }

    if (searchTerm) {
      services = services.filter(service =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filterByBudget) {
      services = services.filter(service =>
        service.basePrice >= filterByBudget.min && service.basePrice <= filterByBudget.max
      );
    }

    if (filterByLocation) {
      services = services.filter(service =>
        service.location.toLowerCase().includes(filterByLocation.toLowerCase())
      );
    }

    services = services.filter(service =>
      service.basePrice >= priceRange.min && service.basePrice <= priceRange.max
    );

    return services.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.basePrice - b.basePrice;
        case "price-high":
          return b.basePrice - a.basePrice;
        case "rating":
          return b.averageRating - a.averageRating;
        case "popularity":
        default:
          return b.reviewCount - a.reviewCount;
      }
    });
  }, [selectedCategory, searchTerm, filterByBudget, filterByLocation, priceRange, sortBy]);

  const handleCategoryClick = (category: ServiceCategory) => {
    setSelectedCategory(category.id === selectedCategory ? null : category.id);
    onCategorySelect?.(category);
  };

  const handleServiceSelect = (service: Service) => {
    if (allowMultiSelect) {
      const newSelection = selectedServices.includes(service.id)
        ? selectedServices.filter(id => id !== service.id)
        : maxSelection && selectedServices.length >= maxSelection
        ? selectedServices
        : [...selectedServices, service.id];

      if (maxSelection && selectedServices.length >= maxSelection && !selectedServices.includes(service.id)) {
        toast({
          title: "Selection limit reached",
          description: `You can only select up to ${maxSelection} services.`,
          variant: "destructive"
        });
        return;
      }

      setSelectedServices(newSelection);
    }

    onServiceSelect?.(service);
  };

  const formatCurrency = (amount: number, currency: string = 'AED') => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Package className="h-5 w-5" />
            Service Categories
          </h2>
          <p className="text-sm text-muted-foreground">
            Explore services by category to find the perfect fit for your event
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search categories and services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popularity">Most Popular</SelectItem>
              <SelectItem value="rating">Highest Rated</SelectItem>
              <SelectItem value="price-low">Price: Low to High</SelectItem>
              <SelectItem value="price-high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Price Range Filter */}
      {showPricing && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Price Range (AED)</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-4">
              <Input
                type="number"
                placeholder="Min"
                value={priceRange.min}
                onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) }))}
                className="w-20"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="number"
                placeholder="Max"
                value={priceRange.max}
                onChange={(e) => setPriceRange(prev => ({ ...prev, max: Number(e.target.value) }))}
                className="w-20"
              />
              <Badge variant="secondary">
                {formatCurrency(priceRange.min)} - {formatCurrency(priceRange.max)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories Grid */}
      {!selectedCategory && (
        <div>
          <h3 className="text-lg font-medium mb-4">Browse by Category</h3>
          {categoriesLoading ? (
            <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <CardLoading key={i} lines={2} />
              ))}
            </div>
          ) : filteredCategories.length === 0 ? (
            <EmptyState
              icon={Package}
              title="No service categories found"
              description={
                searchTerm
                  ? "No categories match your search."
                  : "There are no service categories yet."
              }
            />
          ) : (
            <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
              {filteredCategories.map((category) => (
                <Card
                  key={category.id}
                  className="cursor-pointer transition-all hover:shadow-md hover:scale-105 group"
                  onClick={() => handleCategoryClick(category)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-base group-hover:text-primary transition-colors">
                        {category.name}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  {category.description && (
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground">
                        {category.description}
                      </p>
                    </CardContent>
                  )}
                  <CardFooter className="pt-3">
                    <Button variant="ghost" size="sm" className="w-full justify-between">
                      Explore Services
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Services Grid/List */}
      {selectedCategory && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedCategory(null)}>
                ← Back to Categories
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h3 className="text-lg font-medium">
                {categories.find(c => c.id === selectedCategory)?.name} Services
              </h3>
              <Badge variant="secondary">{filteredServices.length} results</Badge>
            </div>
          </div>

          <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {filteredServices.map((service) => (
              <Card
                key={service.id}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  selectedServices.includes(service.id) && "ring-2 ring-primary",
                  allowMultiSelect && "hover:ring-1 hover:ring-primary/50"
                )}
                onClick={() => handleServiceSelect(service)}
              >
                {service.images[0] && (
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={service.images[0]}
                      alt={service.name}
                      className="w-full h-full object-cover"
                    />
                    {service.featured && (
                      <Badge className="absolute top-2 left-2 bg-orange-500">
                        <Award className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    <Badge className={cn("absolute top-2 right-2", getAvailabilityColor(service.availability))}>
                      {service.availability} availability
                    </Badge>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base line-clamp-1">{service.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={service.vendorLogo} />
                          <AvatarFallback>{service.vendorName[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-muted-foreground">{service.vendorName}</span>
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          <span className="text-xs">{service.vendorRating}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowServiceDetail(service);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {service.description}
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{service.duration}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>{service.capacity.min}-{service.capacity.max} people</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{service.location}</span>
                      </div>
                      {showPricing && (
                        <div className="font-medium text-primary">
                          {formatCurrency(service.basePrice)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mt-3">
                    {service.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="pt-3">
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-xs">{service.averageRating}</span>
                      <span className="text-xs text-muted-foreground">({service.reviewCount})</span>
                    </div>
                    <Button size="sm" variant="outline">
                      <ShoppingCart className="h-3 w-3 mr-1" />
                      Select Service
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Service Detail Modal */}
      <Dialog open={!!showServiceDetail} onOpenChange={() => setShowServiceDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{showServiceDetail?.name}</DialogTitle>
            <DialogDescription>
              {showServiceDetail?.vendorName} • {showServiceDetail?.categoryName}
            </DialogDescription>
          </DialogHeader>

          {showServiceDetail && (
            <div className="space-y-4">
              <p className="text-sm">{showServiceDetail.description}</p>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Duration:</span> {showServiceDetail.duration}
                </div>
                <div>
                  <span className="font-medium">Capacity:</span> {showServiceDetail.capacity.min}-{showServiceDetail.capacity.max} people
                </div>
                <div>
                  <span className="font-medium">Location:</span> {showServiceDetail.location}
                </div>
                <div>
                  <span className="font-medium">Price:</span> {formatCurrency(showServiceDetail.basePrice)}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {showServiceDetail.tags.map((tag) => (
                  <Badge key={tag} variant="outline">{tag}</Badge>
                ))}
              </div>

              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => handleServiceSelect(showServiceDetail)}>
                  Select This Service
                </Button>
                <Button variant="outline">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Selection Summary */}
      {allowMultiSelect && selectedServices.length > 0 && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-muted-foreground">
                  {maxSelection && `${maxSelection - selectedServices.length} remaining`}
                </p>
              </div>
              <Button size="sm">
                Continue with Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}